import React, { useState, useEffect } from "react";
import {
  Button,
  message,
  Tabs,
  Table,
  Calendar,
  Select
} from "antd";
import { QRCodeSVG } from "qrcode.react";
import { _get, _post } from "../../Utils/Helper";
import QRScanner from "./components/QRScanner";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import dayjs from "dayjs";

const TeacherAttendance: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [attendanceData, setAttendanceData] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState("1");
  const [mode, setMode] = useState<"check-in" | "check-out" | null>(null);
  const [isScanning, setIsScanning] = useState(false);

  const [hasCheckedIn, setHasCheckedIn] = useState(false); 
  const [hasCheckedOut, setHasCheckedOut] = useState(false);

  // Fetch attendance and check if user has already checked in/out
  const fetchAttendance = () => {
    _get(
      "attendance",
      (data) => {
        // ✅ Check if data is valid and contains "results"
        if (data && data.success && Array.isArray(data.results)) {
          setAttendanceData(data.results);
  
          // ✅ Check user's attendance today
          const today = new Date().toISOString().split("T")[0];
          const latestEntry = data.results.find(
            (item: { name: any; date: string; }) => item.name === user.name && item.date === today
          );
  
          if (latestEntry) {
            setHasCheckedIn(!!latestEntry.check_in_time);
            setHasCheckedOut(!!latestEntry.check_out_time && latestEntry.check_out_time !== "00:00:00");
          }
        } else {
          message.error("Invalid attendance data format received.");
        }
      },
      (err) => message.error("Failed to fetch attendance data.")
    );
  };
  

  // Call fetchAttendance on component load
  useEffect(() => {
    fetchAttendance();
  }, []);


  // Handle attendance (Check-in & Check-out)
  const handleAttendance = (type: "check-in" | "check-out") => {
    if (!user || !user.name) { // ✅ Use 'user.name' instead of 'user.teacher_name'
      message.error("Teacher data is unavailable.");
      return;
    }

    const now = new Date();
    now.setHours(now.getHours() + 1); // Adjust timezone issue
    const formattedTime = now.toISOString().slice(11, 19);
    const date = now.toISOString().split("T")[0];

    const payload = {
      name: user.name,  // ✅ Use 'user.name'
      class: user.class_name || "Unknown",
      role: user.role || "Teacher",
      department: user.department || "Education",
      date,
      check_in_time: type === "check-in" ? formattedTime : null,
      check_out_time: type === "check-out" ? formattedTime : null
    };

    const endpoint = type === "check-in" ? "attendance/check-in" : "attendance/check-out";

    _post(
      endpoint,
      payload,
      () => {
        message.success(`${type === "check-in" ? "Checked in" : "Checked out"} successfully.`);
        fetchAttendance();
      },
      (error) => {
        console.error("Attendance error:", error);
        message.error("Failed to update attendance.");
      }
    );
  };


  // QR Code Scanner Function
  const handleScanSuccess = (decodedText: string) => {
    try {
      const scannedUser = JSON.parse(decodedText);

      if (scannedUser.role !== "Teacher") {
        message.error("This QR code does not belong to a teacher.");
        return;
      }

      const now = new Date().toISOString();
      const date = new Date().toISOString().split("T")[0];

      const attendanceData = {
        name: scannedUser.name,
        class: user.class_name || "Unknown",
        role: scannedUser.role,
        department: scannedUser.department || "Education",
        date,
        check_in_time: mode === "check-in" ? now : null,
        check_out_time: mode === "check-out" ? now : null
      };

      _post(
        mode === "check-in" ? "attendance/check-in" : "attendance/check-out",
        attendanceData,
        () => {
          message.success(`${scannedUser.name} ${mode === "check-in" ? "checked in" : "checked out"} successfully.`);
        },
        (err) => {
          console.error("Error saving attendance:", err);
          message.error("Failed to record attendance.");
        }
      );
    } catch (error) {
      console.error("Invalid QR code format:", error);
      message.error("Invalid QR code format.");
    }
  };

  // Attendance Table Columns
  const columns = [
    { title: "ID", dataIndex: "id", key: "id" },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Class", dataIndex: "class", key: "class" },
    { title: "Role", dataIndex: "role", key: "role" },
    { title: "Date", dataIndex: "date", key: "date" },
    { title: "Check-In", dataIndex: "check_in_time", key: "check_in_time" },
    { title: "Check-Out", dataIndex: "check_out_time", key: "check_out_time" },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "1",
              label: "Teacher Information",
              children: (
                <div>
                  {user && (
                    <>
                      <QRCodeSVG value={JSON.stringify(user)} size={200} />
                      <pre>{JSON.stringify(user, null, 2)}</pre>
                    </>
                  )}
                  <Button type="primary" onClick={() => handleAttendance("check-in")} disabled={hasCheckedIn}>
                    {hasCheckedIn ? "Checked In ✅" : "Check In"}
                  </Button>

                  <Button type="default" onClick={() => handleAttendance("check-out")} disabled={!hasCheckedIn || hasCheckedOut}>
                    {hasCheckedOut ? "Checked Out ✅" : "Check Out"}
                  </Button>

                </div>
              )
            },
            {
              key: "2",
              label: "Attendance Calendar",
              children: (
                <Calendar
                  dateCellRender={(value) => {
                    const date = value.format("YYYY-MM-DD");
                    const attendance = attendanceData.find((item) => item.date === date);
                    return attendance ? (
                      <div
                        style={{
                          backgroundColor: "#4caf50",
                          color: "#fff",
                          padding: "5px",
                          borderRadius: "4px",
                          textAlign: "center"
                        }}
                      >
                        ✔ Present <br />
                        {attendance.check_in_time || "No Check-in"} - {attendance.check_out_time || "No Check-out"}
                      </div>
                    ) : null;
                  }}
                />
              )
            },
            {
              key: "3",
              label: "Attendance Details",
              children: (
                <Table dataSource={attendanceData} columns={columns} rowKey="id" bordered />
              )
            },
            {
              key: "4",
              label: "QR Code Scanner",
              children: (
                <div>
                  <Select placeholder="Select Mode" onChange={(value) => setMode(value)}>
                    <Select.Option value="check-in">Check-in</Select.Option>
                    <Select.Option value="check-out">Check-out</Select.Option>
                  </Select>
                  <Button type="primary" disabled={!mode} onClick={() => setIsScanning(true)}>
                    Start Scanning
                  </Button>
                  {isScanning && <QRScanner onScanSuccess={handleScanSuccess} />}
                </div>
              )
            }
          ]}
        />
      </div>
    </div>
  );
};

export default TeacherAttendance;
